import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown20Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { Modal } from "../../components/Modal";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import { SrOnly } from "../../faturas/ColumnFilters";
import { Tooltip } from "../../components/Tooltip";
import { setBankMovementReconciliationExclusion } from "../../movimentos/movimentosApi";
import {
  autoReconcileBankMovements,
  createBankReconciliation,
  deleteBankReconciliation,
  getBankReconciliations,
  getReconcilableBankMovements,
  getReconciliationSuggestions,
  type BankReconciliationDto,
  type ReconcilableBankMovement,
  type ReconciliationSuggestion,
  type SuggestionsVm,
} from "../../reconciliacao/reconciliacaoApi";

// Verde de sucesso: index.css não garante uma classe text-* verde fora dos badges,
// por isso a cor de valores positivos vai inline (fallback se a var não existir).
const SUCCESS_COLOR = "var(--color-success, #16a34a)";

// Tolerância de cêntimo — espelha a semântica do backend (§2).
const CENTS_TOLERANCE = 0.005;

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;


// Montante ainda por reconciliar de um movimento (§2): abs(valor) − reconciliado.
const remainingOf = (m: ReconcilableBankMovement): number =>
  Math.max(0, Math.abs(m.amount) - (m.reconciledAmount ?? 0));

// Fora da reconciliação: transferência entre contas próprias, ou marcado à mão como sem fatura.
const isIgnored = (m: ReconcilableBankMovement): boolean =>
  m.isInternalTransfer || m.isExcludedFromReconciliation;

// Estado de reconciliação — um só conceito, derivado de reconciledAmount/isFullyReconciled,
// com os dois estados "fora da reconciliação" por cima: Interna e Sem fatura.
function ReconStateBadge({ movement }: { movement: ReconcilableBankMovement }) {
  const reconciled = movement.reconciledAmount ?? 0;
  const total = Math.abs(movement.amount);

  let cls = "bg-muted text-secondary-text";
  let label = "Por reconciliar";
  if (movement.isInternalTransfer) {
    label = "Interna";
  } else if (movement.isExcludedFromReconciliation) {
    label = "Sem fatura";
  } else if (movement.isFullyReconciled) {
    cls = "bg-green-500/10 text-green-700";
    label = "Reconciliado";
  } else if (reconciled > CENTS_TOLERANCE) {
    cls = "bg-yellow-500/10 text-yellow-700";
    label = `Parcial ${formatCurrency(reconciled)} / ${formatCurrency(total)}`;
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 ${cls}`}
      style={{
        fontSize: "var(--text-xs)",
        borderRadius: "var(--radius)",
        fontWeight: "var(--font-weight-medium)",
      }}
    >
      {label}
    </span>
  );
}

// Score do suggester (0..1) → badge com % e cor por escalão.
function ScoreBadge({ score }: { score: number }) {
  const pct = Math.round(Math.max(0, Math.min(1, score)) * 100);
  const cls =
    score >= 0.75
      ? "bg-green-500/10 text-green-700"
      : score >= 0.4
        ? "bg-yellow-500/10 text-yellow-700"
        : "bg-muted text-secondary-text";
  return (
    <Tooltip text="Grau de confiança da sugestão">
      <span
        className={`inline-flex items-center px-2 py-0.5 ${cls}`}
        style={{
          fontSize: "var(--text-xs)",
          borderRadius: "var(--radius)",
          fontWeight: "var(--font-weight-medium)",
        }}
      >
        {pct}%
      </span>
    </Tooltip>
  );
}

type FilterMode = "unreconciled" | "reconciled" | "ignored" | "all";

export function ReconciliacaoBancariaView() {
  const [movements, setMovements] = useState<ReconcilableBankMovement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<FilterMode>("unreconciled");

  // Um movimento que ficou 100% reconciliado agora mesmo não desaparece da lista onde o
  // utilizador está a olhar: fica no sítio, com o estado a mudar para Reconciliado, até se
  // trocar de filtro. Desaparecer no clique parecia um erro.
  const [justReconciledIds, setJustReconciledIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );

  const changeMode = (next: FilterMode) => {
    setMode(next);
    setJustReconciledIds(new Set());
  };

  // O que o automatismo ligou ao abrir o ecrã; só se diz quando foi mais do que zero.
  const [autoInfo, setAutoInfo] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  // Detalhe do movimento expandido (uma só linha aberta de cada vez).
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestionsVm | null>(null);
  const [links, setLinks] = useState<BankReconciliationDto[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Reconciliar é um clique: liga com o valor sugerido e fica logo em Ligações, com Desfazer ao
  // lado. Um modal a confirmar o que a linha já dizia era o segundo clique de cada movimento.
  // Por linha (targetKind-targetId): só o botão clicado mostra "A reconciliar…"; os outros ficam
  // apenas desativados, sem parecer que a página inteira está a carregar.
  const [linkingKey, setLinkingKey] = useState<string | null>(null);

  // Modal "Desfazer" uma ligação.
  const [undoTarget, setUndoTarget] = useState<BankReconciliationDto | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const [isTogglingExclusion, setIsTogglingExclusion] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setMovements(await getReconcilableBankMovements());
    } catch (err) {
      setError(errorMessage(err, "Não foi possível carregar os movimentos."));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Ao abrir, o automatismo passa primeiro: as certas (valor ao cêntimo, entidade a bater, sem
    // empate) ligam-se antes de a lista aparecer, e o que se vê é só o que precisa de alguém.
    // Se falhar, a lista carrega na mesma; a próxima sincronização volta a tentar.
    (async () => {
      try {
        const { linked } = await autoReconcileBankMovements();
        if (!cancelled && linked > 0) {
          setAutoInfo(
            `${linked} movimento(s) ficaram reconciliados sozinhos, por a fatura ser certa. As ligações ficam marcadas e podem desfazer-se.`,
          );
        }
      } catch {
        // Fica sem faixa; a lista continua a valer.
      }
      if (!cancelled) await load();
    })();

    return () => {
      cancelled = true;
    };
  }, [load]);

  // Guarda contra respostas fora de ordem (o mesmo padrão do loadSequence dos Movimentos):
  // trocar de linha com um pedido a meio não pode deixar as sugestões da linha antiga por cima
  // da nova — um "Reconciliar" nessas condições ligava a fatura ao movimento errado.
  const detailSequence = useRef(0);
  const expandedIdRef = useRef<number | null>(null);

  const loadDetail = useCallback(async (movementId: number) => {
    const sequence = ++detailSequence.current;
    setIsLoadingDetail(true);
    setDetailError(null);
    try {
      const [vm, existing] = await Promise.all([
        getReconciliationSuggestions(movementId),
        getBankReconciliations(movementId),
      ]);
      if (sequence !== detailSequence.current) return;
      setSuggestions(vm);
      setLinks(existing);
    } catch (err) {
      if (sequence !== detailSequence.current) return;
      setSuggestions(null);
      setLinks([]);
      setDetailError(
        errorMessage(err, "Não foi possível carregar as sugestões deste movimento."),
      );
    } finally {
      if (sequence === detailSequence.current) setIsLoadingDetail(false);
    }
  }, []);

  const toggleExpand = (movement: ReconcilableBankMovement) => {
    // Com uma gravação a meio não se troca de linha: era a janela em que a resposta antiga
    // aterrava na linha nova.
    if (linkingKey != null || isTogglingExclusion || isUndoing) return;
    if (expandedId === movement.id) {
      detailSequence.current++;
      expandedIdRef.current = null;
      setExpandedId(null);
      setSuggestions(null);
      setLinks([]);
      setDetailError(null);
      return;
    }
    expandedIdRef.current = movement.id;
    setExpandedId(movement.id);
    setSuggestions(null);
    setLinks([]);
    loadDetail(movement.id);
  };

  // Filtro cliente: estado (Por reconciliar / Reconciliados / Todos) + pesquisa de texto.
  const normalizedFilter = filter.trim().toLowerCase();
  const shown = useMemo(() => {
    let list = movements;
    if (mode === "unreconciled") {
      // As internas e as marcadas "sem fatura" não estão por reconciliar: estão fora disto.
      // As acabadas de reconciliar ficam à vista até se trocar de filtro (ver justReconciledIds).
      list = list.filter(
        (m) => (!m.isFullyReconciled && !isIgnored(m)) || justReconciledIds.has(m.id),
      );
    } else if (mode === "reconciled") {
      list = list.filter((m) => m.isFullyReconciled && !isIgnored(m));
    } else if (mode === "ignored") {
      list = list.filter(isIgnored);
    }
    if (normalizedFilter !== "") {
      list = list.filter((m) =>
        [m.description, m.accountLabel ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(normalizedFilter),
      );
    }
    return list;
  }, [movements, mode, normalizedFilter, justReconciledIds]);

  // Total ainda por reconciliar nas linhas visíveis (para o resumo, estilo listas da obra).
  // As linhas fora da reconciliação não devem prometer trabalho: não somam.
  const shownRemaining = shown.reduce(
    (sum, m) => (isIgnored(m) ? sum : sum + remainingOf(m)),
    0,
  );

  // --- Sem fatura ----------------------------------------------------------
  const handleSetExclusion = async (
    movement: ReconcilableBankMovement,
    excluded: boolean,
  ) => {
    if (isTogglingExclusion) return;
    setIsTogglingExclusion(true);
    setDetailError(null);
    try {
      await setBankMovementReconciliationExclusion(movement.id, excluded);
      await load(true);
      if (expandedIdRef.current != null) await loadDetail(expandedIdRef.current);
      // A linha sai da vista atual sem isto parecer um falhanço: a faixa diz para onde foi.
      setAutoInfo(
        excluded
          ? `"${movement.description}" ficou marcado como sem fatura: saiu de Por reconciliar. Está no filtro Fora da reconciliação, onde pode desfazer com Voltar a reconciliar.`
          : `"${movement.description}" voltou a Por reconciliar.`,
      );
    } catch (err) {
      setDetailError(
        errorMessage(err, "Não foi possível alterar o estado do movimento."),
      );
    } finally {
      setIsTogglingExclusion(false);
    }
  };

  // --- Reconciliar ---------------------------------------------------------
  // O valor é o sugerido: o mínimo entre o que falta no movimento e o que a fatura tem em dívida.
  // Um movimento maior do que a fatura fica parcial e continua na lista para a fatura seguinte.
  const reconcileNow = async (
    movement: ReconcilableBankMovement,
    suggestion: ReconciliationSuggestion,
  ) => {
    if (linkingKey != null) return;
    setLinkingKey(`${suggestion.targetKind}-${suggestion.targetId}`);
    setDetailError(null);
    try {
      await createBankReconciliation({
        bankMovementId: movement.id,
        targetKind: suggestion.targetKind,
        targetId: suggestion.targetId,
        allocatedAmount: suggestion.suggestedAmount,
        notes: null,
      });
      const remainingAfter = remainingOf(movement) - suggestion.suggestedAmount;
      if (remainingAfter <= CENTS_TOLERANCE) {
        setJustReconciledIds((prev) => new Set(prev).add(movement.id));
      }
      await load(true);
      if (expandedIdRef.current != null) await loadDetail(expandedIdRef.current);
      setAutoInfo(
        remainingAfter <= CENTS_TOLERANCE
          ? `${formatCurrency(suggestion.suggestedAmount)} ligados a "${suggestion.targetDisplayName}". O movimento ficou Reconciliado: sai desta lista quando trocar de filtro. Desfazer está nas Ligações do movimento.`
          : `${formatCurrency(suggestion.suggestedAmount)} ligados a "${suggestion.targetDisplayName}". O movimento ficou Parcial: faltam ${formatCurrency(remainingAfter)} por reconciliar.`,
      );
    } catch (err) {
      setDetailError(errorMessage(err, "Não foi possível reconciliar o movimento."));
    } finally {
      setLinkingKey(null);
    }
  };

  // --- Desfazer ------------------------------------------------------------
  const handleUndo = async () => {
    if (!undoTarget || isUndoing) return;
    setIsUndoing(true);
    setUndoError(null);
    try {
      await deleteBankReconciliation(undoTarget.id);
      setUndoTarget(null);
      await load(true);
      if (expandedIdRef.current != null) await loadDetail(expandedIdRef.current);
      setAutoInfo(
        `Ligação desfeita: ${formatCurrency(undoTarget.allocatedAmount)} voltaram a ficar em dívida na fatura e por reconciliar no movimento.`,
      );
    } catch (err) {
      setUndoError(errorMessage(err, "Não foi possível desfazer a ligação."));
    } finally {
      setIsUndoing(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Reconciliação bancária"
        subtitle="Liga movimentos bancários a faturas emitidas e recebidas. As certas ligam-se sozinhas; as outras propõem-se, com reconciliação parcial."
      />

      {/* A live region fica sempre montada (só o texto entra e sai): montá-la já com texto
          falha o anúncio em alguns leitores de ecrã. */}
      <div aria-live="polite">
        {autoInfo && (
          <div
            className="border border-border bg-muted px-4 py-2 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {autoInfo}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        <ListToolbar
          filter={filter}
          onFilterChange={setFilter}
          placeholder="Filtrar por descrição ou conta…"
          summary={
            <span className="text-secondary-text text-sm">
              {shown.length} movimento(s)
              {shownRemaining > 0
                ? ` · ${formatCurrency(shownRemaining)} por reconciliar`
                : ""}
            </span>
          }
        />

        {/* Filtro rápido de estado */}
        <div className="flex items-center gap-2 flex-wrap">
          {(
            [
              { code: "unreconciled", label: "Por reconciliar" },
              { code: "reconciled", label: "Reconciliados" },
              { code: "ignored", label: "Fora da reconciliação" },
              { code: "all", label: "Todos" },
            ] as { code: FilterMode; label: string }[]
          ).map((chip) => {
            const isActive = mode === chip.code;
            return (
              <button
                key={chip.code}
                type="button"
                onClick={() => changeMode(chip.code)}
                className={`inline-flex items-center gap-1 px-3 py-1 text-sm cursor-pointer ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent/50"
                }`}
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {chip.label}
              </button>
            );
          })}
        </div>

        {error && (
          <div
            role="alert"
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {/* Lista de movimentos — tabela com cabeçalho de colunas (estilo das listas da obra),
            cada linha expande para as ligações + sugestões. */}
        <div
          className="bg-card border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          {isLoading ? (
            <p className="px-4 py-6 text-center text-secondary-text text-sm">
              A carregar…
            </p>
          ) : shown.length === 0 ? (
            <p className="px-4 py-6 text-center text-secondary-text text-sm">
              {filter !== ""
                ? "Sem movimentos para o filtro."
                : mode === "reconciled"
                  ? "Ainda não há movimentos reconciliados."
                  : mode === "unreconciled"
                    ? "Sem movimentos por reconciliar."
                    : mode === "ignored"
                      ? "Nada fora da reconciliação: nem transferências internas nem movimentos marcados como sem fatura."
                      : "Sem movimentos. Os movimentos chegam do banco pelo ecrã Bancos."}
            </p>
          ) : (
            <table className="w-full" style={{ minWidth: 720 }}>
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th scope="col" className="px-4 py-3 w-5">
                    <SrOnly>Expandir</SrOnly>
                  </th>
                  <th scope="col" className="px-4 py-3">Data</th>
                  <th scope="col" className="px-4 py-3">Descrição</th>
                  <th scope="col" className="px-4 py-3">Estado</th>
                  <th scope="col" className="px-4 py-3 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => {
                  const isExpanded = expandedId === m.id;
                  const Chevron = isExpanded
                    ? ChevronDown20Regular
                    : ChevronRight20Regular;
                  return (
                    <Fragment key={m.id}>
                      {/* A linha continua clicável ao rato, mas a semântica de botão vive num
                          botão a sério na primeira célula: um <tr role="button"> perdia as
                          semânticas de tabela nos leitores de ecrã. */}
                      <tr
                        onClick={() => toggleExpand(m)}
                        className="border-b border-border last:border-b-0 hover:bg-accent/30 cursor-pointer"
                      >
                        <td className="px-4 py-3 align-top">
                          <button
                            type="button"
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? "Recolher" : "Expandir"} ${m.description}`}
                            className="inline-flex items-center justify-center p-1 cursor-pointer hover:bg-accent/30"
                            style={{ borderRadius: "var(--radius-button)" }}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpand(m);
                            }}
                          >
                            <Chevron aria-hidden="true" className="w-5 h-5 text-secondary-text" />
                          </button>
                        </td>
                        <td className="px-4 py-3 align-top text-secondary-text text-sm whitespace-nowrap">
                          {formatDateShort(m.movementDate)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className="break-words"
                            style={{ fontWeight: "var(--font-weight-medium)" }}
                          >
                            {m.description}
                          </span>
                          {m.counterpartyName && (
                            <span className="block text-xs text-secondary-text mt-0.5 break-words">
                              {m.counterpartyName}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ReconStateBadge movement={m} />
                        </td>
                        <td
                          className={`px-4 py-3 align-top text-right ${
                            m.amount < 0 ? "text-destructive" : ""
                          }`}
                          style={{
                            fontWeight: "var(--font-weight-medium)",
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                            ...(m.amount < 0 ? {} : { color: SUCCESS_COLOR }),
                          }}
                        >
                          {m.amount > 0 ? "+" : ""}
                          {formatCurrency(m.amount)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-border last:border-b-0">
                          <td colSpan={5}>
                            <MovementDetail
                              movement={m}
                              suggestions={suggestions}
                              links={links}
                              isLoading={isLoadingDetail}
                              error={detailError}
                              linkingKey={linkingKey}
                              exclusionBusy={isTogglingExclusion}
                              onSemFatura={(movement, excluded) =>
                                void handleSetExclusion(movement, excluded)
                              }
                              onConciliar={reconcileNow}
                              onDesfazer={setUndoTarget}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Desfazer ligação */}
      {undoTarget && (
        <Modal
          title="Desfazer ligação"
          onClose={() => setUndoTarget(null)}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setUndoTarget(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isUndoing}
                onClick={handleUndo}
                className="px-4 py-2 min-h-[44px] hover:opacity-90 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  borderRadius: "var(--radius-button)",
                  backgroundColor: "var(--destructive)",
                  color: "var(--destructive-foreground)",
                }}
              >
                {isUndoing ? "A desfazer…" : "Desfazer"}
              </button>
            </>
          }
        >
          {undoError && (
            <div
              role="alert"
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {undoError}
            </div>
          )}
          <p className="text-sm text-foreground break-words">
            Desfazer a ligação de{" "}
            <strong>{formatCurrency(undoTarget.allocatedAmount)}</strong> a{" "}
            <strong>{undoTarget.targetDisplayName}</strong>
            {undoTarget.targetInvoiceNumber ? ` (${undoTarget.targetInvoiceNumber})` : ""}? O
            valor em dívida da fatura é reposto.
          </p>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detalhe expandido de um movimento: ligações existentes + sugestões.
// ---------------------------------------------------------------------------

function MovementDetail({
  movement,
  suggestions,
  links,
  isLoading,
  error,
  linkingKey,
  exclusionBusy,
  onSemFatura,
  onConciliar,
  onDesfazer,
}: {
  movement: ReconcilableBankMovement;
  suggestions: SuggestionsVm | null;
  links: BankReconciliationDto[];
  isLoading: boolean;
  error: string | null;
  /** "targetKind-targetId" da ligação em gravação; os outros botões esperam sem loader. */
  linkingKey: string | null;
  /** A marca "sem fatura" está a ser gravada. */
  exclusionBusy: boolean;
  onSemFatura: (movement: ReconcilableBankMovement, excluded: boolean) => void;
  onConciliar: (
    movement: ReconcilableBankMovement,
    suggestion: ReconciliationSuggestion,
  ) => void;
  onDesfazer: (link: BankReconciliationDto) => void;
}) {
  const remaining = remainingOf(movement);
  const directionLabel =
    movement.amount < 0
      ? "Saída: reconcilia-se com faturas recebidas (fornecedores)"
      : "Entrada: reconcilia-se com faturas emitidas (clientes)";

  // Reconciliar por completo substitui a tabela (e o botão que tinha o foco) pelo texto de
  // estado; sem isto o foco caía no body e quem usa teclado perdia o sítio. Vai para o título
  // das Sugestões, que é onde a resposta ficou escrita.
  const suggestionsHeadingRef = useRef<HTMLHeadingElement>(null);
  const wasFullyReconciled = useRef(remaining <= CENTS_TOLERANCE);
  useEffect(() => {
    const isNowReconciled = remaining <= CENTS_TOLERANCE;
    if (isNowReconciled && !wasFullyReconciled.current) {
      suggestionsHeadingRef.current?.focus();
    }
    wasFullyReconciled.current = isNowReconciled;
  }, [remaining]);

  // Todas as faturas em dívida do sentido certo, já ordenadas pelo motor, para o dropdown de
  // escolha à mão. Carregam-se uma vez por movimento aberto; o filtrar ao teclar é local.
  const [candidates, setCandidates] = useState<ReconciliationSuggestion[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  useEffect(() => {
    if (remaining <= CENTS_TOLERANCE) {
      setCandidates([]);
      return;
    }

    let cancelled = false;
    setCandidatesLoading(true);
    getReconciliationSuggestions(movement.id, null, 200)
      .then((vm) => {
        if (!cancelled) setCandidates(vm.suggestions);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      })
      .finally(() => {
        if (!cancelled) setCandidatesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [movement.id, remaining]);

  const candidateOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      candidates.map((s) => ({
        value: `${s.targetKind}-${s.targetId}`,
        label: `${s.targetDisplayName} · ${s.invoiceNumber ?? "sem número"}`,
        description: `${formatCurrency(s.outstandingAmount)} em dívida · ${s.matchReason}`,
        // Para "175", "175,00" ou "4402" encontrarem a fatura ao teclar.
        keywords: `${s.invoiceNumber ?? ""} ${s.outstandingAmount.toFixed(2)} ${s.outstandingAmount
          .toFixed(2)
          .replace(".", ",")}`,
      })),
    [candidates],
  );

  return (
    <div
      className="px-4 pb-4 space-y-4 border-t border-border"
      style={{ backgroundColor: "var(--muted)" }}
    >
      <p className="text-xs text-secondary-text pt-3">
        {directionLabel} · Falta reconciliar neste movimento: {formatCurrency(remaining)} de{" "}
        {formatCurrency(Math.abs(movement.amount))}
      </p>

      {/* Fora da reconciliação: os dois estados explicam-se por extenso, e o "sem fatura"
          desfaz-se aqui mesmo. */}
      {movement.isInternalTransfer && (
        <p className="text-sm text-secondary-text">
          Transferência entre contas próprias: o dinheiro não saiu da empresa, não há fatura do
          outro lado. Fica fora da reconciliação.
        </p>
      )}
      {!movement.isInternalTransfer && movement.isExcludedFromReconciliation && (
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-secondary-text">
            Marcado como sem fatura: este movimento não vai reconciliar com nenhum valor.
          </p>
          <button
            type="button"
            className="inline-flex items-center px-3 py-2 border border-border bg-background text-foreground text-sm cursor-pointer hover:bg-accent/30 disabled:opacity-50"
            style={{ borderRadius: "var(--radius-button)" }}
            disabled={exclusionBusy}
            onClick={() => onSemFatura(movement, false)}
          >
            {exclusionBusy ? "A guardar…" : "Voltar a reconciliar"}
          </button>
        </div>
      )}
      {!isIgnored(movement) && remaining > CENTS_TOLERANCE && links.length === 0 && (
        <div>
          <button
            type="button"
            className="inline-flex items-center px-3 py-2 border border-border bg-background text-foreground text-sm cursor-pointer hover:bg-accent/30 disabled:opacity-50"
            style={{ borderRadius: "var(--radius-button)" }}
            disabled={exclusionBusy}
            title="Este movimento nunca vai ter fatura (ex.: imposto sem documento na app, despesa pessoal). Sai da lista de por reconciliar; pode desfazer-se."
            onClick={() => onSemFatura(movement, true)}
          >
            {exclusionBusy ? "A guardar…" : "Sem fatura: não reconciliar"}
          </button>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {/* Ligações já existentes */}
      {links.length > 0 && (
        <div className="space-y-2">
          <h4
            className="text-foreground"
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semi-bold)",
            }}
          >
            Ligações
          </h4>
          <div
            className="bg-card border border-border"
            style={{ borderRadius: "var(--radius)" }}
          >
            {links.map((link) => (
              <div
                key={link.id}
                className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-b-0 flex-wrap"
              >
                <div className="flex-1 min-w-0">
                  <span
                    className="break-words"
                    style={{ fontWeight: "var(--font-weight-medium)" }}
                  >
                    {link.targetDisplayName}
                  </span>
                  <span className="block text-xs text-secondary-text mt-0.5">
                    {link.targetInvoiceNumber ?? "—"}
                    {link.targetInvoiceDate
                      ? ` · ${formatDateShort(link.targetInvoiceDate)}`
                      : ""}
                  </span>
                </div>
                <span
                  className="whitespace-nowrap"
                  style={{
                    fontWeight: "var(--font-weight-medium)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatCurrency(link.allocatedAmount)}
                </span>
                <button
                  type="button"
                  aria-label={`Desfazer ligação a ${link.targetDisplayName}`}
                  className="text-destructive underline text-sm px-2 py-2"
                  onClick={() => onDesfazer(link)}
                >
                  Desfazer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Escolher à mão. O motor propõe em baixo; quem já sabe qual é a fatura escolhe-a aqui,
          e o dropdown filtra ao teclar por número, entidade ou valor. */}
      {!isIgnored(movement) && remaining > CENTS_TOLERANCE && (
        <SearchableSelect<string>
          label="Procurar fatura"
          labelClassName="text-foreground"
          labelStyle={{
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
          panelPosition="fixed"
          disabled={linkingKey != null}
          value={null}
          onChange={(value) => {
            const chosen = candidates.find((s) => `${s.targetKind}-${s.targetId}` === value);
            if (chosen) onConciliar(movement, chosen);
          }}
          options={candidateOptions}
          loading={candidatesLoading}
          placeholder={
            movement.amount < 0
              ? "Escolhe a fatura recebida a reconciliar"
              : "Escolhe a fatura emitida a reconciliar"
          }
          searchPlaceholder="Número, entidade ou valor…"
          emptyMessage="Nenhuma fatura em dívida bate com o texto."
          style={{ maxWidth: 560 }}
        />
      )}

      {/* Sugestões — não para movimentos fora da reconciliação. */}
      {!isIgnored(movement) && (
      <div className="space-y-2">
        <h4
          ref={suggestionsHeadingRef}
          tabIndex={-1}
          className="text-foreground"
          style={{
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
        >
          Sugestões
        </h4>
        {/* A regra só se explica enquanto há ligações por fazer; num movimento fechado era ruído. */}
        {remaining > CENTS_TOLERANCE && (
          <p className="text-xs text-secondary-text">
            Cada ligação usa o menor valor dos dois lados: o que falta neste movimento ou o que a
            fatura ainda tem em dívida. Um movimento maior do que a fatura fica Parcial e continua
            na lista para a fatura seguinte.
          </p>
        )}

        {isLoading ? (
          <p className="text-sm text-secondary-text py-2">A procurar faturas…</p>
        ) : remaining <= CENTS_TOLERANCE ? (
          <p className="text-sm text-secondary-text py-2">
            Movimento totalmente reconciliado.
          </p>
        ) : !suggestions || suggestions.suggestions.length === 0 ? (
          <p className="text-sm text-secondary-text py-2">
            Sem sugestões com confiança. Use a caixa Procurar fatura, acima, por número, entidade ou valor.
          </p>
        ) : (
          <SuggestionTable
            movement={movement}
            items={suggestions.suggestions}
            linkingKey={linkingKey}
            onConciliar={onConciliar}
          />
        )}
      </div>
      )}

    </div>
  );
}

/**
 * A tabela de faturas candidatas, a mesma para as sugestões do motor e para os resultados da
 * procura: quem lê não tem de aprender dois formatos, e a acção de reconciliar é uma só.
 */
function SuggestionTable({
  movement,
  items,
  linkingKey,
  onConciliar,
}: {
  movement: ReconcilableBankMovement;
  items: ReconciliationSuggestion[];
  linkingKey: string | null;
  onConciliar: (
    movement: ReconcilableBankMovement,
    suggestion: ReconciliationSuggestion,
  ) => void;
}) {
  return (
    <div
      className="bg-card border border-border overflow-x-auto"
      style={{ borderRadius: "var(--radius)" }}
    >
      <table className="w-full" style={{ minWidth: 760 }}>
        <thead>
          <tr className="border-b border-border bg-muted text-left">
            <th scope="col" className="px-3 py-2">Fatura</th>
            <th scope="col" className="px-3 py-2" style={{ width: 110 }}>Data</th>
            <th scope="col" className="px-3 py-2 text-right" style={{ width: 140 }}>
              Em dívida na fatura
            </th>
            <th scope="col" className="px-3 py-2 text-right" style={{ width: 120 }}>A ligar</th>
            <th scope="col" className="px-3 py-2" style={{ width: 220 }}>Motivo</th>
            <th scope="col" className="px-3 py-2" style={{ width: 160 }}>Ação</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr
              key={`${s.targetKind}-${s.targetId}`}
              className="border-b border-border last:border-b-0"
              style={{ verticalAlign: "top" }}
            >
              <td className="px-3 py-2 break-words">
                <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                  {s.targetDisplayName}
                </span>
                {s.invoiceNumber &&
                  (s.targetKind === "client_invoice" ? (
                    <Link
                      to={`/contas-a-receber?faturaNumero=${encodeURIComponent(
                        s.invoiceNumber,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-primary underline mt-0.5"
                    >
                      {s.invoiceNumber}
                    </Link>
                  ) : (
                    <span className="block text-xs text-secondary-text mt-0.5">
                      {s.invoiceNumber}
                    </span>
                  ))}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {formatDateShort(s.invoiceDate)}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {formatCurrency(s.outstandingAmount)}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                  {formatCurrency(s.suggestedAmount)}
                </span>
              </td>
              <td className="px-3 py-2">
                <span className="flex items-center gap-2 flex-wrap">
                  <ScoreBadge score={s.score} />
                  <span className="text-xs text-secondary-text break-words">
                    {s.matchReason}
                  </span>
                </span>
              </td>
              <td className="px-3 py-2">
                {/* O botão diz o valor: "Reconciliar 123,00 €" responde à pergunta "o que é que
                    este clique faz?" sem obrigar a cruzar colunas. */}
                <button
                  type="button"
                  aria-label={`Reconciliar ${formatCurrency(s.suggestedAmount)} com ${s.targetDisplayName}`}
                  className="inline-flex items-center justify-center px-3 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "var(--radius-button)" }}
                  disabled={linkingKey != null}
                  onClick={() => onConciliar(movement, s)}
                >
                  {linkingKey === `${s.targetKind}-${s.targetId}`
                    ? "A reconciliar…"
                    : `Reconciliar ${formatCurrency(s.suggestedAmount)}`}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
